import { ZodError, ZodIssue } from 'zod';

/**
 * Custom error type for ATC manifest validation failures
 * 
 * This error provides:
 * - Clear error message about what failed
 * - Structured validation issues for debugging
 * - Easy serialization for logging
 */
export class AtcManifestValidationError extends Error {
  public readonly code = 'ATC_MANIFEST_VALIDATION_ERROR';
  public readonly issues: ZodIssue[];
  public readonly manifestType: 'main' | 'station';
  public readonly url?: string;
  public readonly rawData?: unknown;

  constructor(
    zodError: ZodError,
    manifestType: 'main' | 'station',
    url?: string,
    rawData?: unknown
  ) {
    const issuesSummary = zodError.issues
      .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
      .join('; ');

    const message = `Invalid ${manifestType} manifest${url ? ` from ${url}` : ''}: ${issuesSummary}`;

    super(message);
    this.name = 'AtcManifestValidationError';
    this.issues = zodError.issues;
    this.manifestType = manifestType;
    this.url = url;
    this.rawData = rawData;

    // Maintains proper stack trace for V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AtcManifestValidationError);
    }
  }

  /**
   * Get a loggable object representation of the error
   */
  toLogObject(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      manifestType: this.manifestType,
      url: this.url,
      issueCount: this.issues.length,
      issues: this.issues.map((issue) => ({
        path: issue.path.join('.'),
        code: issue.code,
        message: issue.message,
      })),
    };
  }

  /**
   * Get formatted validation issues for display
   */
  getFormattedIssues(): string[] {
    return this.issues.map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
      return `${path}${issue.message}`;
    });
  }

  /**
   * Check if this is a validation error
   */
  static isAtcManifestValidationError(error: unknown): error is AtcManifestValidationError {
    return error instanceof AtcManifestValidationError;
  }
}

/**
 * Create a validation error for the main manifest
 */
export function createMainManifestValidationError(
  zodError: ZodError,
  url?: string,
  rawData?: unknown
): AtcManifestValidationError {
  return new AtcManifestValidationError(zodError, 'main', url, rawData);
}

/**
 * Create a validation error for a station manifest
 */
export function createStationManifestValidationError(
  zodError: ZodError,
  url?: string,
  rawData?: unknown
): AtcManifestValidationError {
  return new AtcManifestValidationError(zodError, 'station', url, rawData);
}
